import { Grid } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ContractStateDto,
  ContractStatusDto,
  useGetMyNeedsWaitingQuery,
  useListContractsQuery,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { ChooseEventDepartmentModal } from "../../common/departments/ChooseEventDepartmentModal";
import { useCreateSourcingEvent } from "../../common/departments/useCreateSourcingEvent";
import { useCreateProcurementWithAi } from "../../common/agent/useCreateProcurementWithAi";
import { urls } from "../../urls";
import { useCreateNeed } from "../../common/needs/useCreateNeed";
import type { HeroAction, HeroMetric } from "./homeConfig";
import { heroUsesMetric } from "./heroMetrics";
import { HeroActionCard } from "./HeroActionCard";
import { useHomeLabel } from "./useHomeLabel";

const CONTRACTS_COUNT_LIMIT = 100;

export const heroTracks = (cardCount: number): string =>
  ["1fr", "1.35fr 1fr", "1.35fr 1fr 1fr"][Math.min(Math.max(cardCount, 1), 3) - 1];

export const HeroActions = ({ actions }: { actions: HeroAction[] }) => {
  const { t } = useTranslation();
  const homeLabel = useHomeLabel();
  const navigate = useNavigate();
  const authState = useLoggedInWithOrgContextState();
  const { create: createNeed } = useCreateNeed();
  const createEvent = useCreateSourcingEvent((eventId) => navigate(urls.app.sourcingEvents.edit.go(eventId).info));
  const createWithAi = useCreateProcurementWithAi();

  const wantsQueue = heroUsesMetric(actions, "needsQueue");
  const wantsContracts = heroUsesMetric(actions, "activeContracts");
  const { data: needsWaiting } = useGetMyNeedsWaitingQuery(wantsQueue ? {} : skipToken);
  const { data: activeContracts } = useListContractsQuery(
    wantsContracts
      ? {
          orgId: authState.selectedOrg.id,
          state: ContractStateDto.Published,
          // contracts' statuses is a single comma-joined string (ListContractsApiArg.statuses: string),
          // unlike events' statuses (string[]). Don't "normalize" to an array.
          statuses: [ContractStatusDto.Active, ContractStatusDto.Awarded].join(","),
          limit: CONTRACTS_COUNT_LIMIT,
        }
      : skipToken
  );

  const metricValue = (m?: HeroMetric): number | undefined => {
    if (m === "needsQueue") return needsWaiting?.canTakeNeeds ? needsWaiting.queueCount : undefined;
    if (m === "activeContracts") return activeContracts?.contracts.length;
    return undefined;
  };

  const sublabelFor = (a: HeroAction): string => {
    const v = metricValue(a.metric);
    if (v === undefined) return homeLabel(a.sublabelKey);
    if (a.metric === "needsQueue") return t("{{count}} needs nobody has taken", { count: v });
    if (a.metric === "activeContracts")
      return v >= CONTRACTS_COUNT_LIMIT ? t("100+ active contracts") : t("{{count}} active contracts", { count: v });
    return homeLabel(a.sublabelKey);
  };

  const runTarget = (a: HeroAction) => {
    if (a.target.kind === "navigate") { navigate(a.target.to); return; }
    if (a.target.action === "createSourcingEvent") (createWithAi ?? createEvent.start)();
    else void createNeed();
  };

  return (
    <>
      <Grid templateColumns={{ base: "1fr", md: heroTracks(actions.length) }} gap="3" mb="6">
        {actions.map((a) => (
          <HeroActionCard
            key={a.key}
            labelKey={a.labelKey}
            sublabel={sublabelFor(a)}
            icon={a.icon}
            primary={a.variant === "primary"}
            badge={a.metric === "needsQueue" ? metricValue("needsQueue") : undefined}
            onClick={() => runTarget(a)}
          />
        ))}
      </Grid>
      {createEvent.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={createEvent.departments}
          isCreating={createEvent.isCreating}
          onCancel={createEvent.cancelChoosingDepartment}
          onConfirm={createEvent.confirmDepartment}
        />
      )}
    </>
  );
};
